import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Layers } from "lucide-react";
import { MicroLabel } from "@/components/DataPrimitives";

export type TierTone = "universal" | "core" | "industry" | "customer";

export interface TierSegment {
  /** Unique dataKey for this segment (used by recharts' stackId grouping). */
  key: string;
  /** Short name shown in the legend, e.g. "Foundation & Universal Entities". */
  label: string;
  /** One-line explanation shown in the legend + tooltip, e.g. "party-core, geography-core, …". */
  sublabel: string;
  /** Number of ontology modules this segment represents. */
  count: number;
  tone: TierTone;
}

const TONE_COLOR: Record<TierTone, string> = {
  universal: "hsl(var(--good))",
  core: "hsl(var(--accent) / 0.45)",
  industry: "hsl(var(--accent))",
  customer: "hsl(var(--warn))",
};

/**
 * A single 100%-stacked horizontal bar showing how much of a composed
 * ontology is already-built shared substrate vs. industry-specific vs.
 * customer-thin. Built on Recharts rather than hand-rolled divs so the
 * proportions, tooltip and legend read as a real analytics component.
 */
export function TierStack({ segments, note }: { segments: TierSegment[]; note?: string }) {
  const total = segments.reduce((s, x) => s + x.count, 0) || 1;
  const reused = segments
    .filter((s) => s.tone === "universal" || s.tone === "core")
    .reduce((a, s) => a + s.count, 0);
  const pct = Math.round((reused / total) * 100);
  const row: Record<string, number | string> = { name: "ontology" };
  for (const s of segments) row[s.key] = s.count;

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <MicroLabel className="flex items-center gap-1">
          <Layers size={11} /> Ontology composition
        </MicroLabel>
        <span className="stat-mono text-sm text-good">{pct}% reusable foundation</span>
      </div>

      <div className="overflow-hidden rounded-md border border-border" style={{ height: 30 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={[row]} layout="vertical" barCategoryGap={0} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <XAxis type="number" domain={[0, total]} hide />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              cursor={{ fill: "hsl(var(--text) / 0.04)" }}
              contentStyle={{
                background: "hsl(var(--panel))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 8,
                fontSize: 11,
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                padding: "6px 9px",
              }}
              itemStyle={{ padding: 0 }}
              labelFormatter={() => ""}
              formatter={(value, key) => {
                const seg = segments.find((s) => s.key === key);
                return [`${seg?.sublabel ?? ""}`, `${seg?.label ?? key} \u2014 ${value}`];
              }}
            />
            {segments.map((s) => (
              <Bar key={s.key} dataKey={s.key} stackId="stack" fill={TONE_COLOR[s.tone]} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 text-[11px]">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: TONE_COLOR[s.tone] }} />
            <span className="text-text/90">{s.label}</span>
            <span className="stat-mono text-muted">{s.count}</span>
          </div>
        ))}
      </div>

      <p className="mt-2.5 text-[11px] leading-relaxed text-muted">
        This is a composition mix, not a quality or completion score. {pct}% is reusable
        platform foundation; the remaining {100 - pct}% is the selected industry and
        customer-specific layer. Review labels, mappings, sample data, external standards,
        and the generated <code className="font-mono">ACTIONS_REQUIRED.md</code> before production.
      </p>
      {note && <p className="mt-1.5 text-[11px] leading-relaxed text-muted">{note}</p>}
    </div>
  );
}
